/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useRef, useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'

// Components
import {
    Breadcrumb as ChakraBreadcrumb,
    BreadcrumbItem as ChakraBreadcrumbItem,
    BreadcrumbLink as ChakraBreadcrumbLink,
    // Hooks
    useStyleConfig,
    Box,
} from '@chakra-ui/react'

// Icons
import {LineIcon} from '../icons'

// Others
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useMulticountryUtils from '../../custom-hooks/use-multicountry-utils'

/**
 * A simplification of the Chakra `Breadcrumb` component for our project needs. Given
 * a list of categories, display a breadcrumb and it's items.
 */
const Breadcrumb = ({categories, homepage = false, ...rest}) => {
    const styles = useStyleConfig('Breadcrumb')
    const {buildUrl} = useMultiSite()
    const {buildLocalizedUrl} = useMulticountryUtils()

    const containerRef = useRef(null)
    const [fadeRight, setFadeRight] = useState(false)

    useEffect(() => {
        const el = containerRef.current
        if (!el) return
        const check = () => {
            const overflow = el.scrollWidth > el.clientWidth + 1
            const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1
            setFadeRight(overflow && !atEnd)
        }
        check()
        el.addEventListener('scroll', check, {passive: true})
        window.addEventListener('resize', check)
        return () => {
            el.removeEventListener('scroll', check)
            window.removeEventListener('resize', check)
        }
    }, [])

    return (
        <Box position="relative" width="100%">
            <ChakraBreadcrumb
                ref={containerRef}
                {...styles.container}
                sx={{
                    ...styles.container,
                    display: 'flex',
                    flexWrap: 'nowrap',
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                    '::-webkit-scrollbar': {display: 'none'},
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                }}
                separator={<LineIcon {...styles.icon} />}
                {...rest}
            >
                {categories?.map((category, index) => {
                    const linkStyle =
                        index === categories.length - 1 ? styles.lastLink : styles.link
                    const categoryUrl = category?.slugUrl || category?.c_slugUrl
                    const url = homepage
                        ? buildLocalizedUrl('/', buildUrl)
                        : buildLocalizedUrl(categoryUrl)
                    return (
                        <ChakraBreadcrumbItem
                            key={category.id}
                            data-testid="sf-crumb-item"
                            {...styles.item}
                            sx={{flexShrink: 0}}
                        >
                            <ChakraBreadcrumbLink
                                as={RouteLink}
                                to={url}
                                sx={{...linkStyle, flexShrink: 0}}
                            >
                                {category.name}
                            </ChakraBreadcrumbLink>
                        </ChakraBreadcrumbItem>
                    )
                })}
            </ChakraBreadcrumb>

            {fadeRight && (
                <Box
                    position="absolute"
                    top={0}
                    bottom={0}
                    right={0}
                    width="29%"
                    pointerEvents="none"
                    zIndex={10}
                    bg="linear-gradient(90deg, rgba(255,255,255,0) 0%, #FFF 100%)"
                />
            )}
        </Box>
    )
}

Breadcrumb.displayName = 'Breadcrumb'

Breadcrumb.propTypes = {
    /**
     * The categories to be displayed in this breadcrumb.
     */
    categories: PropTypes.array,
    homepage: PropTypes.bool,
}

export default Breadcrumb
